import type {
  IMySongsLibraryFullLibrarySong,
  IMySongsLibraryFullLibrarySongLesson,
  IMySongsLibraryUserLibraryLesson
} from "./mySongsLibrary.store";
import type {MySongsPageSortColumnType, MySongsPageSortDirectionType} from "../../../../config/my-songs-page-sort";
import {toLowercaseTrim} from "../../../../util/string-util";
import {arrayMax} from "../../../../util/array-math";

export type MySongsPageSongsSortHandlerType = (a: IMySongsLibraryFullLibrarySong, b: IMySongsLibraryFullLibrarySong) => number;
export type MySongsPageLessonsSortHandlerType = (a: IMySongsLibraryUserLibraryLesson, b: IMySongsLibraryUserLibraryLesson) => number;

/**
 * Sort comparison handler
 * @param a
 * @param b
 */
const sortComparisonHandler = <T = unknown>(a: T, b: T): number => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}

const sortComparisonDescHandler = <T = unknown>(a: T, b: T): number =>
  sortComparisonHandler<T>(b, a);

/******* song by song name *******/
const sortSongsByNameAscHandler: MySongsPageSongsSortHandlerType = (a: IMySongsLibraryFullLibrarySong, b: IMySongsLibraryFullLibrarySong): number =>
  sortComparisonHandler<string>(toLowercaseTrim(a.name), toLowercaseTrim(b.name));

const sortSongsByNameDescHandler: MySongsPageSongsSortHandlerType = (a: IMySongsLibraryFullLibrarySong, b: IMySongsLibraryFullLibrarySong): number =>
  sortComparisonDescHandler<string>(toLowercaseTrim(a.name), toLowercaseTrim(b.name));

const getSortSongsByNameHandler = (direction: MySongsPageSortDirectionType): MySongsPageSongsSortHandlerType => {
  if (direction === "asc") {
    return sortSongsByNameAscHandler;
  }
  return sortSongsByNameDescHandler;
}

const sortLessonsBySongNameAscHandler: MySongsPageLessonsSortHandlerType = (a: IMySongsLibraryUserLibraryLesson, b: IMySongsLibraryUserLibraryLesson): number =>
  sortComparisonHandler<string>(toLowercaseTrim(a.songName), toLowercaseTrim(b.songName));

const sortLessonsBySongNameDescHandler: MySongsPageLessonsSortHandlerType = (a: IMySongsLibraryUserLibraryLesson, b: IMySongsLibraryUserLibraryLesson): number =>
  sortComparisonDescHandler<string>(toLowercaseTrim(a.songName), toLowercaseTrim(b.songName));

const getSortLessonsBySongNameHandler = (direction: MySongsPageSortDirectionType): MySongsPageLessonsSortHandlerType => {
  if (direction === "asc") {
    return sortLessonsBySongNameAscHandler;
  }
  return sortLessonsBySongNameDescHandler;
}

/******* song by Artist name *******/
const sortSongsByArtistNameAscHandler: MySongsPageSongsSortHandlerType = (a: IMySongsLibraryFullLibrarySong, b: IMySongsLibraryFullLibrarySong): number =>
  sortComparisonHandler<string>(toLowercaseTrim(a.artistName), toLowercaseTrim(b.artistName));

const sortSongsByArtistNameDescHandler: MySongsPageSongsSortHandlerType = (a: IMySongsLibraryFullLibrarySong, b: IMySongsLibraryFullLibrarySong): number =>
  sortComparisonDescHandler<string>(toLowercaseTrim(a.artistName), toLowercaseTrim(b.artistName));

const getSortSongsByArtistNameHandler = (direction: MySongsPageSortDirectionType): MySongsPageSongsSortHandlerType => {
  if (direction === "asc") {
    return sortSongsByArtistNameAscHandler;
  }
  return sortSongsByArtistNameDescHandler;
}

const sortLessonsByArtistNameAscHandler: MySongsPageLessonsSortHandlerType = (a: IMySongsLibraryUserLibraryLesson, b: IMySongsLibraryUserLibraryLesson): number =>
  sortComparisonHandler<string>(toLowercaseTrim(a.artistName), toLowercaseTrim(b.artistName));

const sortLessonsByArtistNameDescHandler: MySongsPageLessonsSortHandlerType = (a: IMySongsLibraryUserLibraryLesson, b: IMySongsLibraryUserLibraryLesson): number =>
  sortComparisonDescHandler<string>(toLowercaseTrim(a.artistName), toLowercaseTrim(b.artistName));

const getSortLessonsByArtistNameHandler = (direction: MySongsPageSortDirectionType): MySongsPageLessonsSortHandlerType => {
  if (direction === "asc") {
    return sortLessonsByArtistNameAscHandler;
  }
  return sortLessonsByArtistNameDescHandler;
}

/******* song by Popularity *******/
const songPopularityReducer = (accumulatedPopularity: number, lesson: IMySongsLibraryFullLibrarySongLesson) => accumulatedPopularity + lesson.popularity;

const sortSongsByPopularityAscHandler: MySongsPageSongsSortHandlerType = (a: IMySongsLibraryFullLibrarySong, b: IMySongsLibraryFullLibrarySong): number =>
  sortComparisonHandler<number>(a.lessons.reduce(songPopularityReducer, 0), b.lessons.reduce(songPopularityReducer, 0));

const sortSongsByPopularityDescHandler: MySongsPageSongsSortHandlerType = (a: IMySongsLibraryFullLibrarySong, b: IMySongsLibraryFullLibrarySong): number =>
  sortComparisonDescHandler<number>(a.lessons.reduce(songPopularityReducer, 0), b.lessons.reduce(songPopularityReducer, 0));

const getSortSongsByPopularityHandler = (direction: MySongsPageSortDirectionType): MySongsPageSongsSortHandlerType => {
  if (direction === "asc") {
    return sortSongsByPopularityAscHandler;
  }
  return sortSongsByPopularityDescHandler;
}
const sortLessonsByPopularityAscHandler: MySongsPageLessonsSortHandlerType = (a: IMySongsLibraryUserLibraryLesson, b: IMySongsLibraryUserLibraryLesson): number =>
  sortComparisonHandler<number>(a.popularity ?? 0, b.popularity ?? 0);

const sortLessonsByPopularityDescHandler: MySongsPageLessonsSortHandlerType = (a: IMySongsLibraryUserLibraryLesson, b: IMySongsLibraryUserLibraryLesson): number =>
  sortComparisonDescHandler<number>(a.popularity ?? 0, b.popularity ?? 0);

const getSortLessonsByPopularityHandler = (direction: MySongsPageSortDirectionType): MySongsPageLessonsSortHandlerType => {
  if (direction === "asc") {
    return sortLessonsByPopularityAscHandler;
  }
  return sortLessonsByPopularityDescHandler;
}

/******* song by Date Added *******/
const sortSongsByDateAddedAscHandler: MySongsPageSongsSortHandlerType = (a: IMySongsLibraryFullLibrarySong, b: IMySongsLibraryFullLibrarySong): number =>
  sortComparisonHandler<number>(
    arrayMax(a.lessons.map<number>(({dateAdded})=> dateAdded)),
    arrayMax(b.lessons.map<number>(({dateAdded})=> dateAdded))
  );

const sortSongsByDateAddedDescHandler: MySongsPageSongsSortHandlerType = (a: IMySongsLibraryFullLibrarySong, b: IMySongsLibraryFullLibrarySong): number =>
  sortComparisonDescHandler<number>(
    arrayMax(a.lessons.map<number>(({dateAdded})=> dateAdded)),
    arrayMax(b.lessons.map<number>(({dateAdded})=> dateAdded))
  );

const getSortSongsByDateAddedHandler = (direction: MySongsPageSortDirectionType): MySongsPageSongsSortHandlerType => {
  if (direction === "asc") {
    return sortSongsByDateAddedAscHandler;
  }
  return sortSongsByDateAddedDescHandler;
}

const sortLessonsByDateAddedAscHandler: MySongsPageLessonsSortHandlerType = (a: IMySongsLibraryUserLibraryLesson, b: IMySongsLibraryUserLibraryLesson): number =>
  sortComparisonHandler<number>(a.dateAdded, b.dateAdded);

const sortLessonsByDateAddedDescHandler: MySongsPageLessonsSortHandlerType = (a: IMySongsLibraryUserLibraryLesson, b: IMySongsLibraryUserLibraryLesson): number =>
  sortComparisonDescHandler<number>(a.dateAdded, b.dateAdded);

const getSortLessonsByDateAddedHandler = (direction: MySongsPageSortDirectionType): MySongsPageLessonsSortHandlerType => {
  if (direction === "asc") {
    return sortLessonsByDateAddedAscHandler;
  }
  return sortLessonsByDateAddedDescHandler;
}

export const getSortSongsHandler = (column: MySongsPageSortColumnType, direction: MySongsPageSortDirectionType): MySongsPageSongsSortHandlerType | undefined => {
  switch (column){
    case "song.title":
      return getSortSongsByNameHandler(direction);
    case "artist.name":
      return getSortSongsByArtistNameHandler(direction);
    case "song.popularity":
      return getSortSongsByPopularityHandler(direction);
    case "song.dateAdded":
      return getSortSongsByDateAddedHandler(direction);
    default:
      throw Error(`No sort handler defined for column ${column}`);
  }
}

export const getSortLessonHandler = (column: MySongsPageSortColumnType, direction: MySongsPageSortDirectionType): MySongsPageLessonsSortHandlerType | undefined => {
  switch (column){
    case "song.title":
      return getSortLessonsBySongNameHandler(direction);
    case "artist.name":
      return getSortLessonsByArtistNameHandler(direction);
    case "song.popularity":
      return getSortLessonsByPopularityHandler(direction);
    case "song.dateAdded":
      return getSortLessonsByDateAddedHandler(direction);
  }
}
